// Products service layer
import {
  db,
  collection,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  Timestamp,
} from '../repositories/firestore';
import { addInventoryHistory } from './inventory.service';
import { COLLECTIONS } from '../config/appData';

// Default units for dropdown
export const DEFAULT_UNITS = [
  'ชิ้น', 'อัน', 'ด้าม', 'แพ็ก', 'กล่อง', 'ขวด', 'ตัว', 'คู่', 'เมตร', 'กิโลกรัม', 'รีม', 'ม้วน', 'ใบ', 'เล่ม', 'แผ่น'
];

// Default categories for dropdown
export const DEFAULT_CATEGORIES = [
  'เครื่องเขียน', 'อุปกรณ์สำนักงาน', 'เครื่องดื่ม', 'อาหาร', 'เสื้อผ้า',
  'รองเท้า', 'อิเล็กทรอนิกส์', 'เครื่องใช้ไฟฟ้า', 'ของใช้ทั่วไป', 'อื่นๆ'
];

// Default sizes for dropdown
export const DEFAULT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

// Default shoe sizes for dropdown
export const DEFAULT_SHOE_SIZES = [
  '35', '36', '37', '38', '39', '40', '41', '42', '43', '44', '45', '46'
];

// Default colors for dropdown
export const DEFAULT_COLORS = [
  'ดำ',
  'ขาว',
  'เทา',
  'เงิน',
  'ทอง',
  'โรสโกลด์',
  'น้ำเงิน',
  'ฟ้า',
  'เขียว',
  'เหลือง',
  'ส้ม',
  'แดง',
  'ชมพู',
  'ม่วง',
  'น้ำตาล',
  'ครีม',
  'เบจ',
  'โอลีฟ',
  'มินต์'
];

// Helper: Calculate total quantity from variants
export function calculateTotalQuantity(variants) {
  if (!variants || !Array.isArray(variants)) return 0;
  return variants.reduce((sum, v) => sum + (parseInt(v.quantity) || 0), 0);
}

// Helper: Get available sizes from variants
export function getAvailableSizes(variants) {
  if (!variants || !Array.isArray(variants)) return [];
  const sizes = variants.map(v => v.size).filter(Boolean);
  return [...new Set(sizes)];
}

// Helper: Get available colors from variants
export function getAvailableColors(variants) {
  if (!variants || !Array.isArray(variants)) return [];
  const colors = variants.map(v => v.color).filter(Boolean);
  return [...new Set(colors)];
}

// Helper: Get variant by size and color
export function getVariant(product, size, color) {
  if (!product?.variants || !Array.isArray(product.variants)) return null;
  return product.variants.find(v => v.size === size && v.color === color) || null;
}

// Helper: Get available quantity for a variant
export function getVariantAvailableQuantity(variant) {
  if (!variant) return 0;
  return Math.max(0, (variant.quantity || 0) - (variant.reserved || 0));
}

export async function addProduct(productData) {
  try {
    const hasVariants = productData.hasVariants === true && Array.isArray(productData.variants) && productData.variants.length > 0;
    const categoryName = productData.categoryName && typeof productData.categoryName === 'object' ? productData.categoryName : null;
    const categoryId = productData.categoryId || '';
    const category = productData.category || categoryName?.th || 'อื่นๆ';
    const inventoryMode = productData.inventoryMode || 'bulk';
    const specs = productData.specs && typeof productData.specs === 'object' ? productData.specs : null;
    const variationOptions =
      productData.variationOptions && typeof productData.variationOptions === 'object'
        ? productData.variationOptions
        : null;
    
    let data;
    
    if (hasVariants) {
      // Product with variants
      const variants = productData.variants.map(v => ({
        size: v.size || '',
        color: v.color || '',
        quantity: parseInt(v.quantity) || 0,
        costPrice: parseFloat(v.costPrice) || 0,
        sellPrice: parseFloat(v.sellPrice) || 0,
        reserved: 0,
        staffReserved: 0,
      }));
      
      const totalQuantity = calculateTotalQuantity(variants);
      
      data = {
        productName: productData.productName,
        description: productData.description,
        image: productData.image,
        purchaseLocation: productData.purchaseLocation || '',
        addDate: Timestamp.fromDate(new Date(productData.addDate)),
        unit: productData.unit || 'ชิ้น',
        categoryId,
        categoryName,
        category,
        inventoryMode,
        specs,
        variationOptions,
        hasVariants: true,
        variants: variants,
        // Summary fields for easy querying
        quantity: totalQuantity,
        initialQuantity: totalQuantity,
        reserved: 0,
        staffReserved: 0,
        // Use first variant's price as default display price
        price: variants[0]?.sellPrice || 0,
        costPrice: variants[0]?.costPrice || 0,
        sellPrice: variants[0]?.sellPrice || 0,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
    } else {
      // Product without variants (legacy format)
      const costPrice = parseFloat(productData.costPrice) || 0;
      const sellPrice = parseFloat(productData.sellPrice ?? productData.price ?? productData.costPrice) || 0;
      
      data = {
        productName: productData.productName,
        description: productData.description,
        price: sellPrice,
        costPrice: costPrice,
        sellPrice: sellPrice,
        image: productData.image,
        purchaseLocation: productData.purchaseLocation || '',
        addDate: Timestamp.fromDate(new Date(productData.addDate)),
        quantity: parseInt(productData.quantity) || 0,
        initialQuantity: parseInt(productData.quantity) || 0,
        reserved: 0,
        staffReserved: 0,
        unit: productData.unit || 'ชิ้น',
        categoryId,
        categoryName,
        category,
        inventoryMode,
        specs,
        variationOptions,
        hasVariants: false,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
    }
    
    const docRef = await addDoc(collection(db, COLLECTIONS.products), data);
    const productId = docRef.id;
    
    // Add inventory history
    if (hasVariants) {
      for (const v of data.variants) {
        if (v.costPrice && v.quantity) {
          await addInventoryHistory(productId, {
            date: productData.addDate,
            costPrice: v.costPrice,
            quantity: v.quantity,
            type: 'in',
            source: 'admin_add',
            variant: `${v.size}/${v.color}`,
          });
        }
      }
    } else if (productData.costPrice && productData.quantity) {
      await addInventoryHistory(productId, {
        date: productData.addDate,
        costPrice: productData.costPrice,
        quantity: productData.quantity,
        type: 'in',
        source: 'admin_add',
      });
    }
    
    return productId;
  } catch (error) {
    console.error('Error adding product:', error);
    throw error;
  }
}

export async function getAllProducts() {
  try {
    const q = query(collection(db, COLLECTIONS.products), orderBy('createdAt', 'desc'));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error getting products:', error);
    throw error;
  }
}

export async function getProductById(productId) {
  try {
    const docRef = doc(db, COLLECTIONS.products, productId);
    const snap = await getDoc(docRef);
    if (snap.exists()) return { id: snap.id, ...snap.data() };
    throw new Error('Product not found');
  } catch (error) {
    console.error('Error getting product:', error);
    throw error;
  }
}

export async function updateProduct(productId, productData) {
  try {
    const currentSnap = await getDoc(doc(db, COLLECTIONS.products, productId));
    const current = currentSnap.exists() ? currentSnap.data() : {};
    const categoryName = productData.categoryName && typeof productData.categoryName === 'object' ? productData.categoryName : (current?.categoryName ?? null);
    const categoryId = productData.categoryId ?? current?.categoryId ?? '';
    const category = productData.category ?? categoryName?.th ?? current?.category ?? 'อื่นๆ';
    const inventoryMode = productData.inventoryMode ?? current?.inventoryMode ?? 'bulk';
    const specs = productData.specs && typeof productData.specs === 'object' ? productData.specs : (current?.specs ?? null);
    const variationOptions =
      productData.variationOptions && typeof productData.variationOptions === 'object'
        ? productData.variationOptions
        : (current?.variationOptions ?? null);
    
    let addDateValue;
    if (typeof productData.addDate === 'string') {
      addDateValue = Timestamp.fromDate(new Date(productData.addDate));
    } else if (productData.addDate instanceof Date) {
      addDateValue = Timestamp.fromDate(productData.addDate);
    } else {
      addDateValue = productData.addDate;
    }
    
    const hasVariants = productData.hasVariants === true && Array.isArray(productData.variants) && productData.variants.length > 0;
    
    let data;
    
    if (hasVariants) {
      // Product with variants
      const variants = productData.variants.map(v => ({
        size: v.size || '',
        color: v.color || '',
        quantity: parseInt(v.quantity) || 0,
        costPrice: parseFloat(v.costPrice) || 0,
        sellPrice: parseFloat(v.sellPrice) || 0,
        reserved: v.reserved || 0,
        staffReserved: v.staffReserved || 0,
      }));
      
      const totalQuantity = calculateTotalQuantity(variants);
      
      data = {
        productName: productData.productName,
        description: productData.description,
        image: productData.image,
        purchaseLocation: productData.purchaseLocation ?? current?.purchaseLocation ?? '',
        addDate: addDateValue,
        unit: productData.unit ?? current?.unit ?? 'ชิ้น',
        categoryId,
        categoryName,
        category,
        inventoryMode,
        specs,
        variationOptions,
        hasVariants: true,
        variants: variants,
        quantity: totalQuantity,
        initialQuantity: current?.initialQuantity ?? totalQuantity,
        reserved: current?.reserved ?? 0,
        staffReserved: current?.staffReserved ?? 0,
        price: variants[0]?.sellPrice || 0,
        costPrice: variants[0]?.costPrice || 0,
        sellPrice: variants[0]?.sellPrice || 0,
        promotion: productData.promotion || null,
        updatedAt: Timestamp.now(),
      };
    } else {
      // Product without variants
      const costPrice = parseFloat(productData.costPrice ?? productData.price ?? 0);
      const sellPrice = parseFloat(productData.sellPrice ?? productData.price ?? costPrice ?? 0);
      
      data = {
        productName: productData.productName,
        description: productData.description,
        price: sellPrice,
        costPrice: costPrice,
        sellPrice: sellPrice,
        image: productData.image,
        purchaseLocation: productData.purchaseLocation ?? current?.purchaseLocation ?? '',
        addDate: addDateValue,
        quantity: parseInt(productData.quantity) || 0,
        initialQuantity: current?.initialQuantity ?? (parseInt(productData.quantity) || 0),
        unit: productData.unit ?? current?.unit ?? 'ชิ้น',
        categoryId,
        categoryName,
        category,
        inventoryMode,
        specs,
        variationOptions,
        hasVariants: false,
        promotion: productData.promotion || null,
        updatedAt: Timestamp.now(),
      };
    }
    
    const docRef = doc(db, COLLECTIONS.products, productId);
    await updateDoc(docRef, data);
  } catch (error) {
    console.error('Error updating product:', error);
    throw error;
  }
}

export async function deleteProduct(productId) {
  try {
    const docRef = doc(db, COLLECTIONS.products, productId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting product:', error);
    throw error;
  }
}

export async function updateProductQuantity(productId, quantity, isAdd = true) {
  try {
    const product = await getProductById(productId);
    const currentQuantity = product.quantity || 0;
    const change = parseInt(quantity);
    const newQuantity = isAdd ? currentQuantity + change : Math.max(0, currentQuantity - change);
    const docRef = doc(db, COLLECTIONS.products, productId);

    if (isAdd) {
      const reserved = parseInt(product?.reserved || 0);
      const staffReserved = parseInt(product?.staffReserved || 0);
      const currentInitial = parseInt(product?.initialQuantity || 0);
      const nextInitial = Math.max(currentInitial, newQuantity + reserved + staffReserved);
      await updateDoc(docRef, { quantity: newQuantity, initialQuantity: nextInitial, updatedAt: Timestamp.now() });
    } else {
      await updateDoc(docRef, { quantity: newQuantity, updatedAt: Timestamp.now() });
    }

    if (isAdd) {
      await addInventoryHistory(productId, {
        date: new Date(),
        costPrice: product.costPrice || 0,
        quantity: change,
        type: 'in',
        source: 'admin_adjust_inc',
      });
    } else {
      await addInventoryHistory(productId, {
        date: new Date(),
        costPrice: null,
        quantity: change,
        type: 'out',
        source: 'admin_adjust_dec',
      });
    }
  } catch (error) {
    console.error('Error updating product quantity:', error);
    throw error;
  }
}

export function isLowStock(p) {
  const qty = parseInt(p?.quantity || 0);
  const reserved = parseInt(p?.reserved || 0);
  const staffReserved = parseInt(p?.staffReserved || 0);
  const available = Math.max(0, qty - reserved);

  // ใช้ initialQuantity เป็นหลัก; ถ้าไม่มี/เป็น 0 ให้ fallback เป็น “สต๊อกทั้งหมด ณ ตอนนี้”
  // เพื่อไม่ให้สินค้าที่ initialQuantity หาย/ผิดเพี้ยนคำนวณกลับด้าน
  let initial = parseInt(p?.initialQuantity ?? 0);
  if (!initial) initial = Math.max(0, qty + reserved + staffReserved);

  // ตรวจสอบสต๊อกรวม: แจ้งเตือนเมื่อเหลือน้อยกว่า 20%
  if (initial > 0 && available / initial < 0.2) return true;

  // ตรวจสอบแต่ละ variant (ถ้ามี): แจ้งเตือนเมื่อเหลือน้อยกว่า 20% ของ initialQuantity ของ variant
  if (p?.hasVariants && Array.isArray(p?.variants) && p.variants.length > 0) {
    for (const v of p.variants) {
      const vQty = parseInt(v?.quantity || 0);
      const vReserved = parseInt(v?.reserved || 0);
      const vStaffReserved = parseInt(v?.staffReserved || 0);
      const vAvailable = Math.max(0, vQty - vReserved);

      let vInitial = parseInt(v?.initialQuantity ?? 0);
      if (!vInitial) vInitial = Math.max(0, vQty + vReserved + vStaffReserved);

      if (vInitial > 0 && vAvailable / vInitial < 0.2) return true;
    }
  }

  return false;
}

// ดึงรายละเอียด variant ที่สต๊อกต่ำ
export function getLowStockVariants(p) {
  if (!p.hasVariants || !Array.isArray(p.variants)) return [];
  
  return p.variants.filter(v => {
    const vQty = parseInt(v?.quantity || 0);
    const vReserved = parseInt(v?.reserved || 0);
    const vStaffReserved = parseInt(v?.staffReserved || 0);
    const vAvailable = Math.max(0, vQty - vReserved);
    let vInitial = parseInt(v?.initialQuantity ?? 0);
    if (!vInitial) vInitial = Math.max(0, vQty + vReserved + vStaffReserved);
    return vInitial > 0 && vAvailable / vInitial < 0.2;
  }).map(v => ({
    size: v.size,
    color: v.color,
    quantity: v.quantity || 0,
    reserved: v.reserved || 0,
    available: Math.max(0, (v.quantity || 0) - (v.reserved || 0)),
  }));
}

export function getLowStockProducts(list) {
  return (list || []).filter(isLowStock);
}
